import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Tabs,
  Tab,
} from '@mui/material';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { financialApi } from '../services/api';
import { format, subDays } from 'date-fns';

interface Transaction {
  id: number;
  transaction_type: 'income' | 'expense' | 'payment' | 'refund';
  category?: string;
  amount: number;
  description?: string;
  transaction_date: string;
  payment_method?: string;
}

interface Summary {
  total_income: number;
  total_expenses: number;
  total_payments: number;
  net_profit: number;
}

const Financial: React.FC = () => {
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterType, setFilterType] = useState<string>('');
  const [tabValue, setTabValue] = useState(0);
  const [dateRange, setDateRange] = useState({
    start: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
    end: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    fetchData();
  }, [filterType, dateRange]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [transactionsRes, summaryRes, revenueRes] = await Promise.all([
        financialApi.getTransactions({ 
          type: filterType || undefined,
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
        financialApi.getSummary({
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
        financialApi.getRevenue({
          start_date: dateRange.start,
          end_date: dateRange.end,
        }),
      ]);
      setTransactions(transactionsRes.data.data || []);
      setSummary(summaryRes.data.data);
      setRevenueData(revenueRes.data.data || []);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load financial data');
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    const colors: { [key: string]: 'success' | 'error' | 'info' | 'warning' } = {
      income: 'success',
      expense: 'error',
      payment: 'info',
      refund: 'warning',
    };
    return colors[type] || 'default';
  };

  const COLORS = ['#4caf50', '#f44336', '#2196f3', '#ff9800'];

  const chartData = revenueData.map(item => ({
    date: format(new Date(item.date), 'MMM dd'),
    income: item.income,
    expense: item.expense,
    profit: item.profit,
  }));

  const transactionTypeData = [
    { name: 'Income', value: transactions.filter(t => t.transaction_type === 'income').reduce((sum, t) => sum + t.amount, 0) },
    { name: 'Expenses', value: transactions.filter(t => t.transaction_type === 'expense').reduce((sum, t) => sum + t.amount, 0) },
    { name: 'Payments', value: transactions.filter(t => t.transaction_type === 'payment').reduce((sum, t) => sum + t.amount, 0) },
    { name: 'Refunds', value: transactions.filter(t => t.transaction_type === 'refund').reduce((sum, t) => sum + t.amount, 0) },
  ].filter(item => item.value > 0);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Financial Management
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Income</Typography>
                <Typography variant="h4" color="success.main">
                  ${summary.total_income.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Expenses</Typography>
                <Typography variant="h4" color="error.main">
                  ${summary.total_expenses.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Total Payments</Typography>
                <Typography variant="h4" color="info.main">
                  ${summary.total_payments.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>Net Profit</Typography>
                <Typography variant="h4" color={summary.net_profit >= 0 ? 'success.main' : 'error.main'}>
                  ${summary.net_profit.toFixed(2)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          type="date"
          label="Start Date"
          value={dateRange.start}
          onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          type="date"
          label="End Date"
          value={dateRange.end}
          onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 150 }}
        />
        <TextField
          select
          label="Filter by Type"
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All Types</MenuItem>
          <MenuItem value="income">Income</MenuItem>
          <MenuItem value="expense">Expense</MenuItem>
          <MenuItem value="payment">Payment</MenuItem>
          <MenuItem value="refund">Refund</MenuItem>
        </TextField>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(_, newValue) => setTabValue(newValue)}>
          <Tab label="Charts" />
          <Tab label="Transactions" />
        </Tabs>
      </Box>

      {tabValue === 0 && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Revenue Trend</Typography>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Line type="monotone" dataKey="income" stroke="#4caf50" name="Income" />
                      <Line type="monotone" dataKey="expense" stroke="#f44336" name="Expenses" />
                      <Line type="monotone" dataKey="profit" stroke="#2196f3" name="Profit" />
                    </LineChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
                    No revenue data available for the selected period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Transaction Types</Typography>
                {transactionTypeData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={transactionTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${((percent || 0) * 100).toFixed(0)}%`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {transactionTypeData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
                    No transaction data available
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Income vs Expenses</Typography>
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="income" fill="#4caf50" name="Income" />
                      <Bar dataKey="expense" fill="#f44336" name="Expenses" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <Typography color="textSecondary" align="center" sx={{ py: 5 }}>
                    No data available for the selected period
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Amount</TableCell>
                <TableCell>Payment Method</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography color="textSecondary" sx={{ py: 3 }}>
                      No transactions found.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                transactions.map((transaction) => (
                  <TableRow key={transaction.id} hover>
                    <TableCell>
                      {format(new Date(transaction.transaction_date), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={transaction.transaction_type}
                        color={getTypeColor(transaction.transaction_type)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{transaction.category || '-'}</TableCell>
                    <TableCell>{transaction.description || '-'}</TableCell>
                    <TableCell>
                      <Typography
                        color={transaction.transaction_type === 'income' ? 'success.main' : 'error.main'}
                        fontWeight="bold"
                      >
                        {transaction.transaction_type === 'income' ? '+' : '-'}${transaction.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell>{transaction.payment_method || '-'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default Financial;

